/* Optional room video. Artwork, material lighting and shadows remain separate. */
(function(root){
  'use strict';
  class ViuletRoomMotion {
    constructor(renderer, container, onState){
      this.renderer=renderer;this.container=container;this.onState=onState||(()=>{});
      this.document=renderer.canvas.ownerDocument;this.entries=new Map();this.disposed=false;
      this.inView=true;this.suspended=false;this.request=null;this.requestEntry=null;this.requestKind=null;
      this.preference=root.matchMedia?.('(prefers-reduced-motion: reduce)');
      this.connection=root.navigator?.connection;this.listeners=[];this.revision=0;
      this.overlay=this.document.createElement('canvas');this.overlayContext=this.overlay.getContext('2d');this.overlayReady=false;
      for(const[id,background]of Object.entries(renderer.assets.backgrounds||{})){
        if(background.video?.src)this.entries.set(id,{id,spec:background.video,poster:renderer.images.backgrounds[id],intent:null,ready:false,hasFrame:false,failed:false,video:null,canvas:null});
      }
      this.original={draw:renderer._draw,background:renderer._drawBackground,shadow:renderer._drawShadow};
      const controller=this;
      this.wrappers={
        background:function(context,layout){
          const saved=[];
          for(const entry of controller.entries.values())if(entry.hasFrame&&!entry.failed){saved.push([entry.id,this.images.backgrounds[entry.id]]);this.images.backgrounds[entry.id]=entry.canvas;}
          try{return controller.original.background.call(this,context,layout);}
          finally{for(const[id,img]of saved)this.images.backgrounds[id]=img;}
        },
        shadow:function(...args){
          controller.original.shadow.apply(this,args);
          if(controller.overlay.width!==this.canvas.width||controller.overlay.height!==this.canvas.height){controller.overlay.width=this.canvas.width;controller.overlay.height=this.canvas.height;}
          controller.overlayContext.clearRect(0,0,controller.overlay.width,controller.overlay.height);
          controller.overlayContext.drawImage(this.shadowCanvas,0,0);
        },
        draw:function(){
          controller.original.draw.call(this);
          if(!this.images.artwork)return;
          controller.overlayContext.drawImage(this.productCanvas,0,0);
          controller.overlayReady=true;
        }
      };
      renderer._drawBackground=this.wrappers.background;renderer._drawShadow=this.wrappers.shadow;renderer._draw=this.wrappers.draw;
      this.listen(this.document,'visibilitychange',()=>this.sync());
      this.listen(this.preference,'change',()=>{if(this.preference.matches)for(const e of this.entries.values())e.intent=false;this.sync();});
      this.listen(this.connection,'change',()=>this.sync());
      this.listen(root,'pagehide',()=>{this.pageHidden=true;this.sync();});
      this.listen(root,'pageshow',()=>{this.pageHidden=false;this.sync();});
      if(root.IntersectionObserver){this.observer=new root.IntersectionObserver(entries=>{this.inView=entries.some(e=>e.isIntersecting);this.sync();});this.observer.observe(renderer.canvas);}
      renderer._draw();this.sync();
    }
    listen(target,name,handler){if(!target?.addEventListener)return;target.addEventListener(name,handler);this.listeners.push(()=>target.removeEventListener(name,handler));}
    active(){return this.entries.get(this.renderer.background);}
    wants(entry){return entry?.intent===true||(entry?.intent!==false&&!this.preference?.matches&&!this.connection?.saveData);}
    allowed(entry){return !this.disposed&&!this.suspended&&!this.sceneTransition&&!this.pageHidden&&!this.document.hidden&&this.inView&&entry===this.active()&&!entry.failed&&this.wants(entry);}
    notify(){
      const entry=this.active();
      this.onState({available:!!entry,playing:!!entry?.video&&!entry.video.paused,wantsPlay:this.wants(entry),failed:!!entry?.failed,ready:!!entry?.ready});
    }
    ensure(entry){
      if(entry.video)return;
      const video=this.document.createElement('video');entry.video=video;
      entry.canvas=this.document.createElement('canvas');entry.context=entry.canvas.getContext('2d',{alpha:false});
      video.muted=true;video.defaultMuted=true;video.loop=true;video.playsInline=true;video.preload='none';
      video.setAttribute('muted','');video.setAttribute('playsinline','');video.setAttribute('aria-hidden','true');video.tabIndex=-1;
      video.style.cssText='position:absolute;width:1px;height:1px;left:-10000px;top:0;opacity:0;pointer-events:none';
      // Preserve the photographic poster until a decoded playback frame arrives.
      // Some browsers expose loadeddata before a paused video's first frame can be drawn.
      this.listen(video,'loadeddata',()=>{if(this.disposed)return;entry.ready=true;this.sync();});
      this.listen(video,'playing',()=>{this.schedule(entry);this.notify();});
      this.listen(video,'pause',()=>{if(this.requestEntry===entry)this.cancelFrame();this.notify();});
      this.listen(video,'seeked',()=>{this.paint(entry);this.schedule(entry);});
      this.listen(video,'error',()=>{if(this.disposed)return;entry.failed=true;entry.hasFrame=false;entry.ready=false;video.pause();this.renderer._draw();this.sync();});
      this.container.append(video);video.src=entry.spec.src;video.load();
    }
    paint(entry){
      const video=entry.video;
      if(this.disposed||!entry.ready||video.readyState<2||!video.videoWidth)return;
      // Match the visible canvas budget instead of copying the full source frame.
      const ratio=Math.min(1,Math.max(this.renderer.canvas.width,this.renderer.canvas.height)/Math.max(video.videoWidth,video.videoHeight),Math.sqrt(1200000/(video.videoWidth*video.videoHeight)));
      const width=Math.max(1,Math.round(video.videoWidth*ratio)),height=Math.max(1,Math.round(video.videoHeight*ratio));
      if(entry.canvas.width!==width||entry.canvas.height!==height){entry.canvas.width=width;entry.canvas.height=height;}
      entry.context.drawImage(video,0,0,entry.canvas.width,entry.canvas.height);entry.hasFrame=true;
      if(entry!==this.active())return;
      const r=this.renderer;
      // Animated selections own their next full render. Stable selections reuse the
      // exact cached product/shadow pixels, with no repeated material rasterization.
      if(r.frameRequest!==null)return;
      if(this.overlayReady&&this.overlay.width===r.canvas.width&&this.overlay.height===r.canvas.height){
        const ctx=r.context;ctx.globalAlpha=1;ctx.globalCompositeOperation='source-over';ctx.filter='none';
        r._drawBackground(ctx,r._displayedLayout());ctx.save();ctx.setTransform(1,0,0,1,0,0);ctx.drawImage(this.overlay,0,0);ctx.restore();
      }else r._draw();
    }
    cancelFrame(){
      if(this.request===null)return;
      if(this.requestKind==='video')this.requestEntry.video.cancelVideoFrameCallback(this.request);else root.cancelAnimationFrame(this.request);
      this.request=null;this.requestEntry=null;
    }
    schedule(entry){
      if(this.request!==null||!this.allowed(entry)||entry.video.paused||!entry.ready)return;
      this.requestEntry=entry;this.requestKind=typeof entry.video.requestVideoFrameCallback==='function'?'video':'animation';
      const tick=()=>{this.request=null;this.requestEntry=null;if(!this.allowed(entry))return;this.paint(entry);this.schedule(entry);};
      this.request=this.requestKind==='video'?entry.video.requestVideoFrameCallback(tick):root.requestAnimationFrame(tick);
    }
    async sync(){
      if(this.disposed)return;const revision=++this.revision;const active=this.active();
      for(const entry of this.entries.values())if(!this.allowed(entry)&&entry.video){if(this.requestEntry===entry)this.cancelFrame();entry.video.pause();}
      if(active&&this.allowed(active)){
        this.ensure(active);
        if(active.ready){
          try{await active.video.play();if(revision!==this.revision||this.disposed)return;if(this.allowed(active))this.schedule(active);else active.video.pause();}
          catch(error){if(revision!==this.revision||this.disposed)return;if(error.name!=='AbortError')active.intent=false;}
        }
      }
      this.notify();
    }
    toggle(){const entry=this.active();if(!entry)return;entry.intent=entry.failed?true:!this.wants(entry);if(entry.failed){entry.failed=false;entry.video?.load();}this.sync();}
    setSuspended(value){this.suspended=!!value;this.sync();}
    destroy(){
      if(this.disposed)return;this.disposed=true;++this.revision;this.cancelFrame();this.observer?.disconnect();this.listeners.forEach(remove=>remove());
      for(const entry of this.entries.values()){if(entry.video){entry.video.pause();entry.video.removeAttribute('src');entry.video.load();entry.video.remove();}if(entry.canvas)entry.canvas.width=entry.canvas.height=1;}
      const r=this.renderer;if(r._draw===this.wrappers.draw)r._draw=this.original.draw;if(r._drawBackground===this.wrappers.background)r._drawBackground=this.original.background;if(r._drawShadow===this.wrappers.shadow)r._drawShadow=this.original.shadow;
      this.overlay.width=this.overlay.height=1;this.entries.clear();
    }
  }
  root.ViuletRoomMotion=ViuletRoomMotion;
  if(typeof module==='object'&&module.exports)module.exports={ViuletRoomMotion};
})(typeof window==='object'?window:globalThis);
